import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getUsersList, User as APIUser } from '@/js/external-js/variableModules/utils/getUsersList.util';

export interface UserOption {
  value: string;
  label: string;
  email?: string;
  original?: APIUser;
}

interface UsersListContextValue {
  users: UserOption[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const UsersListContext = createContext<UsersListContextValue | undefined>(undefined);

export const UsersListProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUsers = await getUsersList();
      const usersList = apiUsers.map(u => ({
        value: u.userID,
        label: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || u.userID,
        email: u.email,
        original: u,
      }));
      console.log('[UsersListProvider] Fetched users:', usersList);
      setUsers(usersList);
    } catch (err: any) {
      setError(err?.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let retryCount = 0;
    let retryTimeout: NodeJS.Timeout | null = null;
    let cancelled = false;

    const tryFetch = async () => {
      await fetchUsers();
      if (!cancelled && error && retryCount < 10) {
        retryCount++;
        retryTimeout = setTimeout(tryFetch, 10000);
      }
    };

    tryFetch();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [/* only run once on mount */]);

  return (
    <UsersListContext.Provider value={{ users, loading, error, refresh: fetchUsers }}>
      {children}
    </UsersListContext.Provider>
  );
};

export function useUsersList() {
  const ctx = useContext(UsersListContext);
  if (!ctx) throw new Error('useUsersList must be used within a UsersListProvider');
  return ctx;
}
